enum EnvironmentType {
  Development = 0,
  ContinuousIntegration = 1,
  Staging = 2,
  Production = 3,
}

class Environment {
  public get type(): EnvironmentType {
    const testHostAgainstRegex = (regexString: string) => {
      try {
        return new RegExp(regexString).test(window.location.host.toLowerCase());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Invalid RegExp value", error);
        return false;
      }
    };

    if (testHostAgainstRegex(import.meta.env.VITE_PRODUCTION_HOST_REGEX)) {
      return EnvironmentType.Production;
    } else if (testHostAgainstRegex(import.meta.env.VITE_STAGING_HOST_REGEX)) {
      return EnvironmentType.Staging;
    } else if (testHostAgainstRegex(import.meta.env.VITE_CI_HOST_REGEX)) {
      return EnvironmentType.ContinuousIntegration;
    } else {
      return EnvironmentType.Development;
    }
  }

  public get isDevelopment(): boolean {
    return this.type === EnvironmentType.Development;
  }

  public get isContinuousIntegration(): boolean {
    return this.type === EnvironmentType.ContinuousIntegration;
  }

  public get isStaging(): boolean {
    return this.type === EnvironmentType.Staging;
  }

  public get isProduction(): boolean {
    return this.type === EnvironmentType.Production;
  }

  public get firebaseApiKey() {
    return import.meta.env.VITE_FIREBASE_API_KEY ?? "";
  }

  public get firebaseAuthDomain() {
    return import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "";
  }

  public get firebaseTosUrl() {
    return import.meta.env.VITE_TOS_URL?.toString() ?? "";
  }

  public get firebasePrivacyPolicyUrl() {
    return import.meta.env.VITE_PRIVACY_POLICY_URL?.toString() ?? "";
  }

  public get helpPagesUrl() {
    return import.meta.env.VITE_HELP_PAGES_URL?.toString() ?? "";
  }

  public get supportLoginDomain() {
    return import.meta.env.VITE_SUPPORT_HOSTED_DOMAIN;
  }

  public get googleClientId() {
    return import.meta.env.VITE_GOOGLE_CLIENT_ID;
  }

  public get staticMapsKey() {
    return import.meta.env.VITE_API_STATIC_MAPS_KEY;
  }

  public get apiGmapsKey() {
    return import.meta.env.VITE_API_GMAPS_KEY;
  }

  public get brewmanCompanionAppLink() {
    return import.meta.env.VITE_BREWMAN_COMPANION_APP_LINK;
  }

  public get apiGmapsId() {
    return import.meta.env.VITE_API_GMAPS_ID;
  }

  public get companyDomain() {
    return import.meta.env.VITE_COMPANY_DOMAIN;
  }

  public get supportTranslationsUrl() {
    return import.meta.env.VITE_SUPPORT_TRANSLATIONS_URL;
  }

  public get supportLoggingUrl() {
    return import.meta.env.VITE_SUPPORT_LOGGING_URL;
  }

  public get integrationUserEmail() {
    return import.meta.env.VITE_INTEGRATION_USER_EMAIL;
  }

  public get eventLoggerLogEventsToConsoleInDevelopment() {
    return (
      import.meta.env.VITE_EVENT_LOGGER_LOG_EVENTS_TO_CONSOLE_IN_DEVELOPMENT?.toString()
        .toLowerCase()
        .trim() === "true"
    );
  }

  public get loqateApiKey() {
    return import.meta.env.VITE_LOQATE_API_KEY ?? "";
  }

  public get performanceMeasurementsEnabled() {
    return (
      import.meta.env.VITE_ENABLE_PERFORMANCE_MEASUREMENTS?.toString()
        .toLowerCase()
        .trim() === "true"
    );
  }

  public get brewmanAustralianLandingUrl(): string {
    return import.meta.env.VITE_BREWMAN_AUSTRALIAN_HOSTNAME;
  }

  public get brewmanBelgianLandingUrl(): string {
    return import.meta.env.VITE_BREWMAN_BELGIAN_HOSTNAME;
  }

  public get googleAnalyticsEnabled(): boolean {
    return import.meta.env.PROD;
  }

  public get googleAnalyticsBrewmanId(): string {
    return import.meta.env.VITE_GA_BREWMAN_ID;
  }

  public get googleAnalyticsPremierSystemsId(): string {
    return import.meta.env.VITE_GA_PREMIERSYSTEMS_ID;
  }

  public get agGridEnterpriseKey(): string {
    return import.meta.env.VITE_AG_GRID_ENTERPRISE_KEY;
  }
}

export default new Environment();
